/**
 * PortfoliYo Trade — Deployed Strategies (SDO-3).
 *
 * This page was `Strategies.tsx`: 1,552 lines and sixteen API calls, of which SDO-0 classified
 * **eight authoring and one research** against **five deployment, execution and operations**. The
 * authoring half moved to Lab at SDO-1. What is left is the half Trade owns — how much capital a
 * strategy gets, how it is sized by regime, whether its regime data is ready — and that half is
 * *kept*, not collateral damage from a rename.
 *
 * **What is gone, and where it went:**
 *
 * | removed here | now |
 * |---|---|
 * | Add Symphony (API / DSL / portfolio JSON) | Lab's Strategies page (SDO-1) |
 * | Validate, validate-DSL, validate-portfolio | Lab, against the definition being written |
 * | Revalidate dependencies | Lab — revalidation edits authoring state |
 * | Delete strategy | Lab deactivates the strategy it owns |
 * | Per-strategy target-weight preview | Lab's research pages (PB-1 moved the rest) |
 *
 * **What is deliberately not here yet.** Nothing on this page is *pinned*. Trade cannot import a
 * deployment package until SDO-6, so every row below is a strategy that was added directly in Trade
 * before the boundary existed, and it still resolves through the mutable `user_symphonies` row.
 * The provenance panel says exactly that rather than leaving the fields blank — a blank "Version"
 * reads as "we do not know", and the truthful answer is "there is nothing pinned yet".
 *
 * **The provenance panel must not read Lab's version table.** Showing `strategy_versions.version_number`
 * here would render whatever Lab last saved as though it were the deployed version — which is the
 * single failure this milestone exists to prevent, dressed up as a feature. The pin has to come from
 * a Trade-owned imported artifact (SDO-7), and until that exists the honest display is "not imported".
 */
import { useEffect, useRef, useState } from 'react';
import { api, DeploymentImportError } from '../lib/api';
import type {
  DeployedArtifact,
  DeploymentImportResult,
  DeploymentVersionSwitch,
  StrategyDeployment,
  UploadProgress,
} from '../lib/api';
import type { UserSymphony, RegimeBackfillStatus, RegimeMultiplierPolicy, StrategyRegimeMultiplierSettings } from '../types/api';
import { RefreshCw, AlertCircle, CheckCircle, Database, Save, FlaskConical, Upload } from 'lucide-react';
import { formatDateTimeEastern } from '../lib/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { CollapsibleCard } from '../components/CollapsibleCard';

/** Build-time, like the sidebar's product link, and empty (no link) where no Lab is configured. */
const LAB_ORIGIN = (import.meta.env.VITE_LAB_ORIGIN as string | undefined) ?? '';

/**
 * Import a package file: look first, then accept (SDO-6).
 *
 * Two calls, never one with a flag. `previewDeploymentPackage` parses the file, recomputes both
 * hashes and validates the definition through the engine that will execute it — and writes nothing.
 * Only the second button writes. An operator dropping a file into a trading system should see what
 * is in it while it is still outside the database.
 *
 * The refusal is shown by name because the fixes differ: an unknown schema means this Trade is
 * older than the Lab that wrote the file; a hash mismatch means the file was altered after export;
 * a version conflict means Lab produced two definitions calling themselves one version, which is a
 * bug to go and fix rather than anything resolvable here.
 */
const IMPORT_REFUSAL_HEADLINES: Record<string, string> = {
  strategy_definition: 'This is a strategy file, not a deployment package',
  unknown_schema: 'Package is from a newer Lab',
  malformed: 'Not a deployment package',
  hash_mismatch: 'Altered since it was exported',
  dependency_hash_mismatch: 'A sleeve was altered since export',
  bundle_hash_mismatch: 'Sleeves added, removed or re-keyed since export',
  duplicate_dependency: 'Two sleeves under one name',
  missing_definition: 'No definition in the package',
  unsupported_source_mode: 'Nothing executable to deploy',
  unsupported_definition: 'This build cannot execute it',
  version_hash_conflict: 'That version is already here, differently',
  too_large: 'File is larger than Trade imports',
  gateway: 'The upload did not reach Trade',
};

const MB = 1024 * 1024;

/**
 * What an upload in flight is doing, shown for the preview AND the import — each sends the whole
 * file. A large package on a slow link takes the better part of a minute, and a page whose only sign
 * of that is a greyed-out input or a button label reads as stuck; operators re-clicked or gave up.
 * Real byte counts, not an animation alone, so a stalled upload looks stalled.
 */
function UploadStatus({
  action,
  file,
  progress,
}: {
  action: 'preview' | 'import';
  file: File;
  progress: UploadProgress | null;
}) {
  let detail: string;
  let percent: number | null = null;
  if (!progress || progress.phase === 'compressing') {
    detail = `Compressing ${(file.size / MB).toFixed(1)} MB for upload…`;
  } else if (progress.phase === 'uploading') {
    percent = Math.min(100, Math.round((progress.loaded / Math.max(progress.total, 1)) * 100));
    detail = `Uploading — ${(progress.loaded / MB).toFixed(1)} of ${(progress.total / MB).toFixed(1)} MB (${percent}%)`;
  } else {
    percent = 100;
    detail =
      action === 'preview'
        ? 'Uploaded. Trade is recomputing the hashes and validating the definition with the engine that will run it…'
        : 'Uploaded. Trade is re-checking the package and saving it…';
  }

  return (
    <div role="status" aria-live="polite" className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <div className="flex items-center gap-3">
        <RefreshCw className="w-5 h-5 flex-shrink-0 animate-spin text-emerald-700" />
        <div className="min-w-0 text-sm text-emerald-900">
          <p className="font-semibold break-all">
            {action === 'preview' ? 'Checking' : 'Importing'} {file.name}
          </p>
          <p className="mt-0.5">{detail}</p>
        </div>
      </div>
      {percent !== null && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-emerald-100">
          <div
            className={`h-full rounded-full bg-emerald-600 transition-all duration-300 ${
              progress?.phase === 'processing' ? 'animate-pulse' : ''
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}

function ImportPackagePanel({
  onImported,
  onDeploy,
  deployedArtifactIds,
}: {
  onImported: () => void;
  onDeploy: (artifactId: number) => Promise<void>;
  deployedArtifactIds: Set<number>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<DeploymentImportResult | null>(null);
  const [refusal, setRefusal] = useState<{ reason: string | null; message: string } | null>(null);
  const [busy, setBusy] = useState<'preview' | 'import' | 'deploy' | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [imported, setImported] = useState<DeploymentImportResult | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);
  // Remounts the file input after an import, so choosing the same file again fires `onChange`.
  const [inputKey, setInputKey] = useState(0);

  const reset = () => {
    setResult(null);
    setRefusal(null);
    setImported(null);
    setDeployError(null);
  };

  const run = async (phase: 'preview' | 'import', action: () => Promise<void>) => {
    setBusy(phase);
    setProgress(null);
    setRefusal(null);
    try {
      await action();
    } catch (error) {
      if (error instanceof DeploymentImportError) {
        setRefusal({ reason: error.reason, message: error.message });
      } else {
        setRefusal({ reason: null, message: error instanceof Error ? error.message : 'Import failed' });
      }
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const doPreview = (chosen: File) =>
    run('preview', async () => {
      setResult(await api.previewDeploymentPackage(chosen, setProgress));
    });

  const doImport = () =>
    file &&
    run('import', async () => {
      const outcome = await api.importDeploymentPackage(file, setProgress);
      setImported(outcome);
      setResult(null);
      setFile(null);
      setInputKey((key) => key + 1);
      onImported();
    });

  // The last step, offered where the operator already is. "Deploy it below" pointed at a table
  // under the fold, and an import that ends in a success message reads as finished when nothing
  // yet trades it.
  const doDeploy = async (artifactId: number) => {
    setBusy('deploy');
    setDeployError(null);
    try {
      await onDeploy(artifactId);
    } catch (error) {
      setDeployError(error instanceof Error ? error.message : 'Could not deploy this version');
    } finally {
      setBusy(null);
    }
  };

  const summary = result?.summary;
  const importedId = imported?.artifact_id ?? null;
  const importedIsDeployed = importedId !== null && deployedArtifactIds.has(importedId);

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
      <div className="flex items-center gap-2 mb-1">
        <Upload className="w-5 h-5 text-emerald-700" />
        <h2 className="text-xl font-bold text-gray-900">Import a strategy from Lab</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Export a shortlisted version in PortfoliYo Lab, then upload the package file here. Trade
        recomputes its hashes and validates the definition with the engine that will run it. Nothing
        is armed by importing — allocation and execution settings stay yours to set afterwards.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <input
          key={inputKey}
          id="deployment-package-file"
          type="file"
          accept=".json,application/json"
          onChange={(e) => {
            const chosen = e.target.files?.[0] ?? null;
            setFile(chosen);
            reset();
            if (chosen) doPreview(chosen);
          }}
          disabled={busy !== null}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
        />
      </div>

      {(busy === 'preview' || busy === 'import') && file && (
        <UploadStatus action={busy} file={file} progress={progress} />
      )}

      {refusal && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <p className="font-semibold">
              {(refusal.reason && IMPORT_REFUSAL_HEADLINES[refusal.reason]) || 'Import refused'}
            </p>
            <p className="mt-0.5">{refusal.message}</p>
            {refusal.reason === 'strategy_definition' && LAB_ORIGIN && (
              <a
                href={LAB_ORIGIN}
                className="inline-flex items-center gap-1 mt-2 font-semibold text-blue-700 hover:text-blue-900 underline"
              >
                <FlaskConical className="w-4 h-4" />
                Open PortfoliYo Lab
              </a>
            )}
          </div>
        </div>
      )}

      {imported && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900 flex-1">
            {/* Idempotent, not silent (SDO-D3): re-uploading a file you already uploaded is the
                likeliest second import there is, and a screen that appeared to do nothing would be
                indistinguishable from one that failed. */}
            <p className="font-semibold">
              {imported.outcome === 'already_imported'
                ? 'Already imported — nothing changed'
                : 'Imported'}
            </p>
            <p className="mt-0.5">
              {imported.summary.name} v{imported.summary.source_version}
              {imported.outcome === 'already_imported' && imported.imported_at
                ? ` was imported on ${formatDateTimeEastern(imported.imported_at)}.`
                : '.'}{' '}
              {importedIsDeployed
                ? 'It is deployed — disabled, PAPER, 0% allocation. Set its allocation and enable it under Deployments to trade it.'
                : 'Nothing trades it until it is deployed.'}
            </p>
            {importedId !== null && !importedIsDeployed && (
              <button
                onClick={() => doDeploy(importedId)}
                disabled={busy !== null}
                className="mt-3 inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-gray-300 transition-all"
              >
                {busy === 'deploy' && <RefreshCw className="w-4 h-4 animate-spin" />}
                {busy === 'deploy' ? 'Deploying…' : 'Deploy this version'}
              </button>
            )}
            {deployError && <p className="mt-2 text-red-800">{deployError}</p>}
          </div>
        </div>
      )}

      {summary && (
        <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">
              Preview — nothing has been saved yet
            </span>
            {result?.outcome === 'already_imported' && (
              <span className="text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-2 py-0.5">
                Already imported{result.imported_at ? ` on ${formatDateTimeEastern(result.imported_at)}` : ''}
              </span>
            )}
          </div>
          <dl className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-xs">
            <div>
              <dt className="text-gray-500 font-medium">Strategy</dt>
              <dd className="text-gray-900 mt-0.5 font-semibold">
                {summary.name} — version {summary.source_version} ({summary.source_mode})
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 font-medium">Validated</dt>
              <dd className="text-gray-900 mt-0.5">
                {summary.validation_status} · {formatDateTimeEastern(summary.validated_at)}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-gray-500 font-medium">Definition hash</dt>
              {/* Shown in full, not truncated: this is what an operator compares against Lab when
                  something looks wrong, and a shortened hash is a hash you cannot check. */}
              <dd className="text-gray-900 mt-0.5 font-mono break-all">{summary.definition_hash}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-gray-500 font-medium">Bundle hash (definition + sleeves)</dt>
              <dd className="text-gray-900 mt-0.5 font-mono break-all">{summary.bundle_hash}</dd>
            </div>
            <div>
              <dt className="text-gray-500 font-medium">Exported</dt>
              <dd className="text-gray-900 mt-0.5">
                {formatDateTimeEastern(summary.exported_at)} by {summary.exported_by}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 font-medium">Sleeves carried</dt>
              <dd className="text-gray-900 mt-0.5">
                {summary.dependencies.length === 0
                  ? 'None — self-contained definition'
                  : summary.dependencies.map((d) => d.key).join(', ')}
              </dd>
            </div>
          </dl>
          <div className="px-4 pb-4">
            <button
              onClick={doImport}
              disabled={busy !== null}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-gray-300 transition-all"
            >
              {busy === 'import' && <RefreshCw className="w-4 h-4 animate-spin" />}
              {busy === 'import' ? 'Importing…' : result?.outcome === 'already_imported' ? 'Import again' : 'Import this version'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Configured deployments: an artifact, plus how it executes (SDO-7).
 *
 * Allocation, mode and the on/off switch live here and not on the artifact, because changing how
 * much capital a strategy gets must not look like changing the strategy. Switching which version a
 * deployment runs is deliberately absent — that is a different kind of event and gets its own
 * control with its own audit trail (SDO-8).
 */
function Deployments({
  deployments,
  artifacts,
  isMarketHours,
  onChanged,
}: {
  deployments: StrategyDeployment[];
  artifacts: DeployedArtifact[];
  isMarketHours: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allocationDraft, setAllocationDraft] = useState<Record<number, string>>({});
  const [history, setHistory] = useState<Record<number, DeploymentVersionSwitch[]>>({});

  if (deployments.length === 0) return null;

  /** The capital rules the server enforces, checked before the request so the reason is immediate.
   *  This is the allocation that trades (SDO-9) — the pre-deployment Allocate control is gone. */
  const saveAllocation = (deployment: StrategyDeployment) => {
    const allocation = parseFloat(allocationDraft[deployment.id] ?? String(deployment.allocation_percent));
    if (isNaN(allocation) || allocation < 0 || allocation > 100) {
      setError('Allocation must be a number between 0 and 100');
      return;
    }
    const others = deployments
      .filter((d) => d.id !== deployment.id)
      .reduce((sum, d) => sum + d.allocation_percent, 0);
    if (others + allocation > 100) {
      setError(`Total allocation would exceed 100%. Other deployments hold ${others.toFixed(2)}%.`);
      return;
    }
    patch(deployment.id, { allocation_percent: allocation });
  };

  const patch = async (id: number, body: Parameters<typeof api.updateDeployment>[1]) => {
    setBusy(id);
    setError(null);
    try {
      await api.updateDeployment(id, body);
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update the deployment');
    } finally {
      setBusy(null);
    }
  };

  const switchVersion = async (deployment: StrategyDeployment, artifactId: number) => {
    setBusy(deployment.id);
    setError(null);
    try {
      await api.switchDeploymentVersion(deployment.id, artifactId);
      onChanged();
      // Reloaded immediately: the switch is only real to an operator once they can see it in the
      // trail, and that is the half of this feature that has to be believable.
      setHistory((prev) => ({ ...prev, [deployment.id]: [] }));
      showHistory(deployment.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not switch version');
    } finally {
      setBusy(null);
    }
  };

  const showHistory = async (deploymentId: number) => {
    try {
      const rows = await api.getDeploymentHistory(deploymentId);
      setHistory((prev) => ({ ...prev, [deploymentId]: rows }));
    } catch {
      // The trail is context; failing to load it must not break the configuration controls.
    }
  };

  return (
    <div id="deployments" className="bg-white rounded-lg p-6 shadow-md border border-gray-200 scroll-mt-4">
      <h2 className="text-xl font-bold text-gray-900">Deployments</h2>
      <p className="text-sm text-gray-600 mt-1 mb-4">
        A pinned version, and how it runs. This is what the rebalancer trades: every preview, plan,
        paper run, live run and scheduled run resolves the version pinned here, and the definition's
        hash is rechecked each time. Editing the strategy in Lab cannot change what a deployment
        executes — that takes importing a new version and switching to it deliberately.
      </p>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {deployments.map((deployment) => (
          <div key={deployment.id} className="p-4 rounded-lg border-2 border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-bold text-gray-900">{deployment.name}</span>
              <span className="text-xs font-semibold text-gray-700 bg-gray-100 rounded px-2 py-0.5">
                v{deployment.source_version}
              </span>
              <span className={`text-xs font-bold rounded px-2 py-0.5 ${
                deployment.trading_mode === 'LIVE'
                  ? 'bg-red-100 text-red-800 border border-red-300'
                  : 'bg-blue-100 text-blue-800 border border-blue-300'
              }`}>
                {deployment.trading_mode}
              </span>
              <span className={`text-xs font-bold rounded px-2 py-0.5 ${
                deployment.is_enabled
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}>
                {deployment.is_enabled ? 'Enabled' : 'Disabled'}
              </span>
              <span className="text-xs font-mono text-gray-500 break-all">
                {deployment.definition_hash}
              </span>
              {/* Fail-closed, made visible. Execution refuses this deployment; without saying so
                  here, the only symptom is an order that never happened. */}
              {!deployment.definition_intact && (
                <span className="inline-flex items-center gap-1 text-xs font-bold rounded px-2 py-0.5 bg-red-100 text-red-800 border border-red-300">
                  <AlertCircle className="w-3 h-3" />
                  Definition does not match its hash — this deployment will not trade
                </span>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-700">Allocation</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={allocationDraft[deployment.id] ?? deployment.allocation_percent}
                  onChange={(e) =>
                    setAllocationDraft((prev) => ({ ...prev, [deployment.id]: e.target.value }))
                  }
                  disabled={deployment.is_enabled && isMarketHours}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                />
                <span className="text-gray-600">%</span>
                <button
                  onClick={() => saveAllocation(deployment)}
                  disabled={busy === deployment.id || (deployment.is_enabled && isMarketHours)}
                  className="px-3 py-1 text-sm font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-300"
                >
                  Save
                </button>
              </label>

              <label className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-gray-700">Mode</span>
                <select
                  value={deployment.trading_mode}
                  onChange={(e) => patch(deployment.id, { trading_mode: e.target.value })}
                  disabled={busy === deployment.id || (deployment.is_enabled && isMarketHours)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm bg-white disabled:bg-gray-100"
                >
                  <option value="PAPER">PAPER</option>
                  <option value="LIVE">LIVE</option>
                </select>
              </label>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={deployment.is_enabled}
                  onChange={(e) => patch(deployment.id, { is_enabled: e.target.checked })}
                  disabled={busy === deployment.id}
                  className="w-4 h-4 rounded border-gray-300 text-emerald-600"
                />
                <span className="font-semibold text-gray-700">Enabled</span>
              </label>
            </div>

            {/* The same rule the server applies (`_refuse_during_market_hours`), shown before a click
                rather than after it. Enabled stays usable: disabling is how an operator stops a
                deployment, and making that harder during the session would be dangerous. */}
            {deployment.is_enabled && isMarketHours && (
              <p className="mt-2 text-xs text-amber-800">
                Allocation and mode are locked during market hours (9:30 AM – 4:00 PM ET) while this
                deployment is enabled. Disable it first, or change them after the close.
              </p>
            )}

            {/* The version switcher is its own control, separated from the settings above, because
                it changes WHAT executes rather than how much of it. Only other imported versions of
                THIS strategy are offered — switching to a different strategy is a new deployment,
                so that this one's history stays the history of one thing. */}
            <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-gray-700">Version</span>
              <select
                value={deployment.artifact_id}
                onChange={(e) => switchVersion(deployment, Number(e.target.value))}
                disabled={busy === deployment.id}
                className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
              >
                {artifacts
                  .filter((a) => a.source_strategy_id === deployment.source_strategy_id)
                  .sort((a, b) => b.source_version - a.source_version)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      v{a.source_version}
                      {a.id === deployment.artifact_id ? ' (running)' : ''}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={() => showHistory(deployment.id)}
                className="text-xs font-semibold text-blue-700 hover:text-blue-900 underline"
              >
                Version history
              </button>
              {deployment.is_enabled && (
                <span className="text-xs text-amber-800">
                  Enabled — switching is locked during market hours
                </span>
              )}
            </div>

            {history[deployment.id]?.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-gray-600">
                {history[deployment.id].map((row) => (
                  <li key={row.id} className="font-mono">
                    {row.from_source_version === null
                      ? `deployed v${row.to_source_version}`
                      : `v${row.from_source_version} → v${row.to_source_version}`}
                    <span className="text-gray-400">
                      {' '}· {formatDateTimeEastern(row.switched_at)}
                      {row.switched_by ? ` · ${row.switched_by}` : ''}
                    </span>
                    {row.note && <span className="font-sans text-gray-700"> — {row.note}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * What has been imported, and the honest note that it is not yet what executes.
 *
 * Execution still resolves the mutable strategy rows below; pinning every execution path to these
 * artifacts is SDO-9. Saying so is better than a screen that implies the pin is already in force.
 */
function ImportedArtifacts({
  artifacts,
  deployedArtifactIds,
  onDeploy,
}: {
  artifacts: DeployedArtifact[];
  deployedArtifactIds: Set<number>;
  onDeploy: (artifactId: number) => void;
}) {
  if (artifacts.length === 0) return null;

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
      <h2 className="text-xl font-bold text-gray-900">Imported versions</h2>
      <p className="text-sm text-gray-600 mt-1 mb-4">
        Immutable copies of what Lab exported. Editing the strategy in Lab cannot change these — a
        new version means a new import. Deploying one creates a disabled, PAPER, zero-allocation
        deployment; enable it and give it an allocation and it becomes what the rebalancer trades.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left">
              <th className="px-3 py-2 font-semibold text-gray-600">Strategy</th>
              <th className="px-3 py-2 font-semibold text-gray-600">Version</th>
              <th className="px-3 py-2 font-semibold text-gray-600">Definition hash</th>
              <th className="px-3 py-2 font-semibold text-gray-600">Sleeves</th>
              <th className="px-3 py-2 font-semibold text-gray-600">Imported</th>
              <th className="px-3 py-2 font-semibold text-gray-600"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {artifacts.map((artifact) => (
              <tr key={artifact.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <span className="font-semibold text-gray-900">{artifact.name}</span>
                  <span className="block text-gray-500 font-mono">{artifact.source_strategy_id}</span>
                </td>
                <td className="px-3 py-2 font-semibold text-gray-900">v{artifact.source_version}</td>
                <td className="px-3 py-2 font-mono text-gray-700 break-all max-w-[280px]">
                  {artifact.definition_hash}
                </td>
                <td className="px-3 py-2 text-gray-700">{artifact.dependency_count || '—'}</td>
                <td className="px-3 py-2 text-gray-700">
                  {artifact.imported_at ? formatDateTimeEastern(artifact.imported_at) : '—'}
                  {artifact.imported_by && (
                    <span className="block text-gray-500">by {artifact.imported_by}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {deployedArtifactIds.has(artifact.id) ? (
                    <span className="text-gray-500">Deployed</span>
                  ) : (
                    <button
                      onClick={() => onDeploy(artifact.id)}
                      className="px-3 py-1 text-xs font-semibold rounded bg-emerald-700 text-white hover:bg-emerald-800"
                    >
                      Deploy
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DeployedStrategies() {
  const { isAdmin } = useAuth();
  const [symphonies, setSymphonies] = useState<UserSymphony[]>([]);
  const [artifacts, setArtifacts] = useState<DeployedArtifact[]>([]);
  const [deployments, setDeployments] = useState<StrategyDeployment[]>([]);
  const scrollToDeploymentsRef = useRef(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Locks allocation and mode on ENABLED deployments during the session (the server's rule).
  const [isMarketHours, setIsMarketHours] = useState(false);

  // Regime backfill status for DSL strategies
  const [regimeStatuses, setRegimeStatuses] = useState<Record<number, RegimeBackfillStatus>>({});
  const regimePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [triggeringBackfill, setTriggeringBackfill] = useState<Record<number, boolean>>({});

  // Regime multiplier settings per strategy
  const [regimePolicies, setRegimePolicies] = useState<RegimeMultiplierPolicy[]>([]);
  const [regimeSettings, setRegimeSettings] = useState<Record<number, StrategyRegimeMultiplierSettings>>({});
  const [savingRegime, setSavingRegime] = useState<Record<number, boolean>>({});
  const [regimeSavedOk, setRegimeSavedOk] = useState<Record<number, boolean>>({});
  const [loadingRegimeSettings, setLoadingRegimeSettings] = useState<Record<number, boolean>>({});

  const loadSymphonies = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSymphonies();
      setSymphonies(data.symphonies);
      // Don't auto-select - let user choose which strategy to view
      // Kick off regime status check for all DSL strategies
      const dslStrategies = data.symphonies.filter(s => s.source_mode === 'dsl');
      if (dslStrategies.length > 0) {
        await fetchRegimeStatuses(dslStrategies.map(s => s.id));
      }
    } catch (error) {
      console.error('Error loading symphonies:', error);
      setError('Failed to load deployed strategies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRegimeSettings = async (strategyId: number) => {
    if (regimeSettings[strategyId] !== undefined) return;
    setLoadingRegimeSettings(prev => ({ ...prev, [strategyId]: true }));
    try {
      const data = await api.getStrategyRegimeMultiplierSettings(strategyId);
      setRegimeSettings(prev => ({ ...prev, [strategyId]: data }));
    } catch {
      setRegimeSettings(prev => ({ ...prev, [strategyId]: { strategy_id: strategyId, is_enabled: false, apply_to_backtest: true, apply_to_live_execution: false } }));
    } finally {
      setLoadingRegimeSettings(prev => ({ ...prev, [strategyId]: false }));
    }
  };

  const handleSaveRegimeSettings = async (strategyId: number) => {
    const settings = regimeSettings[strategyId];
    if (!settings) return;
    setSavingRegime(prev => ({ ...prev, [strategyId]: true }));
    try {
      const updated = await api.upsertStrategyRegimeMultiplierSettings(strategyId, {
        policy_id: settings.policy?.policy_id ?? null,
        is_enabled: settings.is_enabled,
        apply_to_backtest: settings.apply_to_backtest,
        apply_to_live_execution: settings.apply_to_live_execution,
      });
      setRegimeSettings(prev => ({ ...prev, [strategyId]: updated }));
      setRegimeSavedOk(prev => ({ ...prev, [strategyId]: true }));
      setTimeout(() => setRegimeSavedOk(prev => ({ ...prev, [strategyId]: false })), 3000);
    } catch (err) {
      console.error('Failed to save regime settings:', err);
    } finally {
      setSavingRegime(prev => ({ ...prev, [strategyId]: false }));
    }
  };

  const fetchRegimeStatuses = async (ids: number[]) => {
    const results = await Promise.allSettled(
      ids.map(id => api.getSymphonyRegimeBackfillStatus(id).then(status => ({ id, status })))
    );
    const updates: Record<number, RegimeBackfillStatus> = {};
    for (const r of results) {
      if (r.status === 'fulfilled') {
        updates[r.value.id] = r.value.status;
      }
    }
    setRegimeStatuses(prev => ({ ...prev, ...updates }));
    return updates;
  };

  const handleTriggerRegimeBackfill = async (symphonyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setTriggeringBackfill(prev => ({ ...prev, [symphonyId]: true }));
    setRegimeStatuses(prev => ({
      ...prev,
      [symphonyId]: { ...prev[symphonyId], status: 'computing' },
    }));
    try {
      await api.triggerSymphonyRegimeBackfill(symphonyId);
    } catch (err) {
      console.error('Failed to trigger regime backfill:', err);
      setRegimeStatuses(prev => ({
        ...prev,
        [symphonyId]: { ...prev[symphonyId], status: prev[symphonyId]?.status ?? 'computing' },
      }));
    } finally {
      setTriggeringBackfill(prev => ({ ...prev, [symphonyId]: false }));
    }
  };

  const loadDeployments = () =>
    api.getDeployments()
      .then(setDeployments)
      .catch(() => {});

  /** Throws, so each caller reports the failure where the operator clicked. */
  const deploy = async (artifactId: number) => {
    await api.createDeployment(artifactId);
    scrollToDeploymentsRef.current = true;
    await loadDeployments();
  };

  const deployFromTable = async (artifactId: number) => {
    setError(null);
    try {
      await deploy(artifactId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not deploy this version');
    }
  };

  const deployedArtifactIds = new Set(deployments.map((d) => d.artifact_id));

  const loadArtifacts = () => {
    api.getDeployedArtifacts()
      .then(setArtifacts)
      // Degrades rather than failing the page: the imported list is context, and a deployment
      // screen that would not render because a secondary list failed is worse than one missing it.
      .catch(() => {});
  };

  useEffect(() => {
    loadSymphonies();
    loadArtifacts();
    loadDeployments();
    api.getRegimeMultiplierPolicies()
      .then((res) => setRegimePolicies(res.policies ?? []))
      .catch(() => {});
    api.getMarketStatus()
      .then((status) => setIsMarketHours(status.is_market_hours))
      .catch(() => {});
  }, []);

  // After a deploy, bring its new row into view: allocation and the enable switch are the rest of
  // deploying. An effect rather than a scroll after the fetch, because the section does not exist
  // until the render that follows it — the first deployment is what makes it appear.
  useEffect(() => {
    if (!scrollToDeploymentsRef.current) return;
    scrollToDeploymentsRef.current = false;
    document.getElementById('deployments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [deployments]);

  useEffect(() => {
    if (selectedStrategyId) {
      loadRegimeSettings(selectedStrategyId);
    }
  }, [selectedStrategyId, symphonies]);

  // Poll regime status for DSL strategies that are still "computing"
  useEffect(() => {
    if (regimePollRef.current) {
      clearInterval(regimePollRef.current);
      regimePollRef.current = null;
    }

    const computingIds = symphonies
      .filter(s => s.source_mode === 'dsl')
      .filter(s => {
        const st = regimeStatuses[s.id];
        return st && st.engine_enabled && st.status === 'computing';
      })
      .map(s => s.id);

    if (computingIds.length === 0) return;

    regimePollRef.current = setInterval(async () => {
      const updated = await fetchRegimeStatuses(computingIds);
      const stillComputing = computingIds.filter(id => {
        const st = updated[id];
        return st && st.status === 'computing';
      });
      if (stillComputing.length === 0 && regimePollRef.current) {
        clearInterval(regimePollRef.current);
        regimePollRef.current = null;
      }
    }, 10000);

    return () => {
      if (regimePollRef.current) {
        clearInterval(regimePollRef.current);
        regimePollRef.current = null;
      }
    };
  }, [symphonies, regimeStatuses]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading deployed strategies...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Deployed Strategies</h1>
          <p className="text-gray-600 mt-1 text-base">
            Allocation, execution configuration and regime sizing for the strategies this account trades
          </p>
        </div>
      </div>

      {/* Where authoring went. Rendered rather than left to a release note: a user who came here to
          add a strategy needs to be told where the button moved, not shown a page missing one. */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <FlaskConical className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 space-y-1">
          <p className="font-semibold">Strategies are written in PortfoliYo Lab, not here.</p>
          <p>
            Creating, editing, validating and backtesting a strategy all live in Lab. Trade deploys a
            fixed version of one — it never edits a definition. Export a shortlisted version from Lab
            and import the file below.
          </p>
        </div>
      </div>

      <ImportPackagePanel
        onImported={loadArtifacts}
        onDeploy={deploy}
        deployedArtifactIds={deployedArtifactIds}
      />

      <Deployments
        deployments={deployments}
        artifacts={artifacts}
        isMarketHours={isMarketHours}
        onChanged={loadDeployments}
      />

      <ImportedArtifacts
        artifacts={artifacts}
        deployedArtifactIds={deployedArtifactIds}
        onDeploy={deployFromTable}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 text-base">{error}</p>
        </div>
      )}

      {symphonies.length === 0 ? (
        // The empty state of the pre-import list below, so only when the page is empty as a whole:
        // shown beside a deployment that was just made, it contradicted the row above it.
        deployments.length === 0 && artifacts.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-gray-700 text-lg">
              No deployed strategies. Author one in PortfoliYo Lab, then deploy it here.
            </p>
          </div>
        )
      ) : (
        <>
          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            {/* Named for what it is. This list was titled "Deployments" beside the real Deployments
                card, and its Allocate button set an allocation the rebalancer no longer reads (SDO-9)
                — while the boot-time cutover could turn it into an enabled deployment. It stays for
                the regime settings and backfill, which are still keyed on these rows. */}
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900">Added before Lab import — not traded</h2>
              <p className="text-sm text-gray-600 mt-1">
                Strategies added directly in Trade before strategies were imported from Lab. The
                rebalancer trades only the deployments above, so these have no allocation to set. To
                trade one, export it from Lab, import it here and deploy it.
              </p>
            </div>
            <div className="space-y-3">
              {symphonies.map((symphony) => (
                <div
                  key={symphony.id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedStrategyId === symphony.id
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="flex-1 cursor-pointer select-none"
                      onClick={() => setSelectedStrategyId(
                        selectedStrategyId === symphony.id ? null : symphony.id
                      )}
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-bold text-lg text-gray-900">
                          {symphony.name || symphony.symphony_id}
                        </span>
                        {selectedStrategyId === symphony.id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-600 text-white">
                            Selected
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                          symphony.source_mode === 'dsl'
                            ? 'bg-blue-100 text-blue-700'
                            : symphony.source_mode === 'portfolio'
                            ? 'bg-violet-100 text-violet-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {symphony.source_mode === 'dsl' ? 'DSL Code' : symphony.source_mode === 'portfolio' ? 'Portfolio' : 'API'}
                        </span>
                        {/* Validation is REPORTED here and never re-run. Revalidating a portfolio's
                            sleeve dependencies rewrites authoring state, so the fix belongs in Lab
                            (SDO-0 §2 classifies /symphonies/{id}/revalidate as AUTHORING). */}
                        {symphony.source_mode === 'portfolio' && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${
                            symphony.validation_status === 'valid' && !symphony.needs_revalidation
                              ? 'bg-green-50 text-green-700 border-green-300'
                              : symphony.validation_status === 'invalid'
                              ? 'bg-red-50 text-red-700 border-red-300'
                              : symphony.needs_revalidation || symphony.validation_status === 'stale'
                              ? 'bg-orange-50 text-orange-700 border-orange-300'
                              : 'bg-amber-50 text-amber-700 border-amber-300'
                          }`}>
                            {symphony.needs_revalidation || symphony.validation_status === 'stale' ? (
                              <><AlertCircle className="w-3 h-3" />Sleeve changed — revalidate in Lab</>
                            ) : symphony.validation_status === 'valid' ? (
                              <><CheckCircle className="w-3 h-3" />All sleeves resolved</>
                            ) : symphony.validation_status === 'invalid' ? (
                              <><AlertCircle className="w-3 h-3" />Missing sleeves</>
                            ) : (
                              <><AlertCircle className="w-3 h-3" />Pending validation</>
                            )}
                          </span>
                        )}
                        {/* Regime backfill status badge */}
                        {symphony.source_mode === 'dsl' && (() => {
                          const rs = regimeStatuses[symphony.id];
                          if (!rs || !rs.engine_enabled || rs.status === 'not_applicable') return null;
                          const isTriggering = triggeringBackfill[symphony.id];
                          const isComputing = rs.status === 'computing';
                          return (
                            <span className="inline-flex items-center gap-1">
                              {isComputing ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  Regime data: Computing...
                                </span>
                              ) : rs.status === 'ready' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                                  <Database className="w-3 h-3" />
                                  Regime data: Ready
                                </span>
                              ) : null}
                              {isAdmin && (
                                <button
                                  onClick={(e) => handleTriggerRegimeBackfill(symphony.id, e)}
                                  disabled={isTriggering}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700 border border-indigo-300 hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Re-run regime score backfill for this strategy"
                                >
                                  <RefreshCw className={`w-3 h-3 ${isTriggering ? 'animate-spin' : ''}`} />
                                  Re-run regime backfill
                                </button>
                              )}
                            </span>
                          );
                        })()}
                      </div>
                      {symphony.name && (
                        <p className="text-sm text-gray-600 mt-1">ID: {symphony.symphony_id}</p>
                      )}
                      {symphony.description && (
                        <p className="text-sm text-gray-600 mt-1">{symphony.description}</p>
                      )}

                      {/* What Trade is executing, and where it came from.
                          Every field is deliberately "Not imported" until SDO-6/7 give Trade an
                          immutable artifact of its own to read them off. Filling them from Lab's
                          strategy_versions table would show whatever Lab last saved as the deployed
                          version — the exact substitution the pin exists to prevent. */}
                      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3" onClick={(e) => e.stopPropagation()}>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                          Deployment provenance
                        </p>
                        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                          <div>
                            <dt className="text-gray-500 font-medium">Pinned version</dt>
                            <dd className="text-gray-800 font-mono mt-0.5">Not imported</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500 font-medium">Definition hash</dt>
                            <dd className="text-gray-800 font-mono mt-0.5">Not imported</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500 font-medium">Imported at / by</dt>
                            <dd className="text-gray-800 font-mono mt-0.5">Not imported</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500 font-medium">Validation</dt>
                            <dd className="text-gray-800 mt-0.5">
                              {symphony.validation_status || 'unknown'}
                              {symphony.last_validated_at && (
                                <span className="text-gray-500">
                                  {' '}· {formatDateTimeEastern(symphony.last_validated_at)}
                                </span>
                              )}
                            </dd>
                          </div>
                        </dl>
                        <p className="text-xs text-gray-500 mt-2">
                          Added directly in Trade on {formatDateTimeEastern(symphony.created_at)}, before
                          strategies were imported from Lab. Nothing trades it: the rebalancer runs
                          pinned deployments only (SDO-9), so an edit in Lab cannot change an order.
                        </p>
                      </div>

                      {/* Sleeve composition, read-only: it is what the deployment will trade. */}
                      {symphony.source_mode === 'portfolio' && symphony.dependency_json && symphony.dependency_json.length > 0 && (
                        <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
                          <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 border-b border-gray-200 flex items-center justify-between">
                            <span>Sleeve Dependencies</span>
                            {symphony.last_validated_at && (
                              <span className="text-gray-400 font-normal">
                                Validated {new Date(symphony.last_validated_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-3 py-1 text-gray-500 font-medium">Status</th>
                                <th className="text-left px-3 py-1 text-gray-500 font-medium">Sleeve</th>
                                <th className="text-right px-3 py-1 text-gray-500 font-medium">Bullish %</th>
                                <th className="text-right px-3 py-1 text-gray-500 font-medium">Risk-off %</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {symphony.dependency_json.map((dep) => (
                                <tr key={dep.key} className="hover:bg-gray-50">
                                  <td className="px-3 py-1.5">
                                    {dep.status === 'resolved' ? (
                                      <CheckCircle className="w-3 h-3 text-green-500" />
                                    ) : dep.status === 'ambiguous' ? (
                                      <AlertCircle className="w-3 h-3 text-orange-500" />
                                    ) : (
                                      <AlertCircle className="w-3 h-3 text-amber-500" />
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <span className="font-mono text-gray-700 truncate block max-w-[160px]" title={dep.key}>
                                      {dep.resolved_name || dep.key}
                                    </span>
                                    {dep.status === 'ambiguous' && (
                                      <span className="text-orange-600 text-xs">
                                        {dep.ambiguous_matches} matches — needs unique name or root_id
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-medium text-emerald-700">
                                    {dep.weight_when_true != null ? `${(dep.weight_when_true * 100).toFixed(0)}%` : '—'}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-medium text-blue-700">
                                    {dep.weight_else != null ? `${(dep.weight_else * 100).toFixed(0)}%` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {symphony.validation_status === 'invalid' && symphony.validation_errors && symphony.validation_errors.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {symphony.validation_errors.map((err, i) => (
                            <p key={i} className="text-xs text-red-600">• {err}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Read-only. An old allocation is shown for what it is — recorded, not traded —
                      rather than hidden, so nobody wonders where the percentage went. */}
                  {symphony.allocation_percent > 0 && (
                    <p className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-500">
                      Old allocation {symphony.allocation_percent.toFixed(2)}% — recorded before
                      deployments, not traded.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Regime Multiplier Settings */}
          {selectedStrategyId && (
            <CollapsibleCard
              title="Regime Position Multiplier Settings"
              defaultOpen={false}
            >
              <div className="p-6">
                {loadingRegimeSettings[selectedStrategyId] ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Loading settings…
                  </div>
                ) : (() => {
                  const settings = regimeSettings[selectedStrategyId];
                  if (!settings) return null;

                  const updateSettings = (patch: Partial<StrategyRegimeMultiplierSettings>) => {
                    setRegimeSettings(prev => ({
                      ...prev,
                      [selectedStrategyId]: { ...prev[selectedStrategyId], ...patch },
                    }));
                  };

                  return (
                    <div className="space-y-5">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.is_enabled}
                            onChange={(e) => updateSettings({ is_enabled: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <span className="text-sm font-semibold text-gray-700">Enable regime multiplier for this strategy</span>
                            <p className="text-xs text-gray-400">When enabled, position sizes will be scaled by the regime policy before rebalancing.</p>
                          </div>
                        </label>
                      </div>

                      {settings.is_enabled && (
                        <>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Policy</label>
                            <select
                              value={settings.policy?.policy_id ?? ''}
                              onChange={(e) => {
                                const pol = regimePolicies.find(p => p.policy_id === e.target.value) ?? null;
                                updateSettings({ policy: pol });
                              }}
                              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white"
                            >
                              <option value="">— Use default policy —</option>
                              {regimePolicies.map((p) => (
                                <option key={p.policy_id} value={p.policy_id}>
                                  {p.name} ({p.policy_id}){p.is_default ? ' [default]' : ''}
                                </option>
                              ))}
                            </select>
                            {settings.policy && (
                              <p className="text-xs text-gray-500 mt-1">
                                Bullish ×{settings.policy.bullish_factor} · Neutral ×{settings.policy.neutral_factor} · Bearish ×{settings.policy.bearish_factor}
                                {settings.policy.normalize_weights ? ' · Normalize on' : ` · Cash → ${settings.policy.cash_fallback_symbol}`}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col sm:flex-row gap-4">
                            <label className="flex items-center gap-2.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={settings.apply_to_backtest}
                                onChange={(e) => updateSettings({ apply_to_backtest: e.target.checked })}
                                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-700">Apply to backtests</span>
                                <p className="text-xs text-gray-400">Use multiplier when running historical backtests.</p>
                              </div>
                            </label>
                            <div className="flex items-center gap-2.5 opacity-50 cursor-not-allowed select-none">
                              <input
                                type="checkbox"
                                disabled
                                checked={false}
                                className="w-4 h-4 rounded border-gray-300 text-gray-400"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-500">Apply to live execution</span>
                                <p className="text-xs text-gray-400">Coming soon — live order scaling is not yet wired.</p>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="flex items-center gap-3 pt-1 flex-wrap">
                        <button
                          onClick={() => handleSaveRegimeSettings(selectedStrategyId)}
                          disabled={savingRegime[selectedStrategyId]}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          {savingRegime[selectedStrategyId] ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          {savingRegime[selectedStrategyId] ? 'Saving…' : 'Save Settings'}
                        </button>
                        {regimeSavedOk[selectedStrategyId] && (
                          <span className="flex items-center gap-1.5 text-sm text-emerald-700 font-medium">
                            <CheckCircle className="w-4 h-4" />
                            Saved
                          </span>
                        )}
                      </div>

                      {(settings.policy || settings.updated_at) && (
                        <div className="mt-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500 space-y-0.5">
                          {settings.policy && (
                            <p>
                              <span className="font-semibold text-gray-700">Applied policy:</span>{' '}
                              {settings.policy.name}
                              <span className="text-gray-400 font-mono ml-1">({settings.policy.policy_id})</span>
                            </p>
                          )}
                          {settings.updated_at && (
                            <p>
                              <span className="font-semibold text-gray-700">Last updated:</span>{' '}
                              {new Date(settings.updated_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </CollapsibleCard>
          )}
        </>
      )}
    </div>
  );
}
